/**
 * DevPage - Developer Tools
 *
 * AI-agent and developer friendly tools page:
 * - Database inspector (accounts, transactions, categories)
 * - CSV Import with detailed progress
 * - Data management (clear, export, seed)
 * - Quick links to /log page
 * - Sync settings (auto-sync configuration)
 * - Debug info display
 *
 * SECURITY: This page is restricted to owner only (nova223@gmail.com)
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Card, CardHeader, Button } from '@/components/shared';
import { db, getDBStats, Account, Transaction, Category } from '@services/db';
import {
  checkConnection,
  getSyncStatus,
  pushAll,
  pull,
  fullSync,
  getApiUrl,
  setApiUrl,
  getLastSyncAt,
  resetSyncState,
  forceFreshPull,
} from '@services/sync';
import { supabase } from '@services/supabase';
import { devlog } from '@services/devlog';
import { useOwnerAccess } from '@/hooks/useOwnerAccess';
import { SyncSettingsPanel } from './SyncSettingsPanel';
import { ImportTab } from './ImportTab';
import { DataTab } from './DataTab';
import './DevPage.css';

type TabId = 'overview' | 'import' | 'data' | 'sync' | 'subscription' | 'accounts' | 'transactions' | 'categories';

export default function DevPage() {
  const { isOwner, isLoading: authLoading, userEmail } = useOwnerAccess();

  // SECURITY: Redirect non-owners
  if (authLoading) {
    return <div className="loading">Checking access...</div>;
  }

  if (!isOwner) {
    console.warn('[DEV] Unauthorized access attempt:', userEmail);
    return <Navigate to="/" replace />;
  }

  return <DevPageContent />;
}

function DevPageContent() {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [stats, setStats] = useState<{
    accounts: number;
    transactions: number;
    categories: number;
    budgets: number;
  } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Refresh stats
  const refreshStats = useCallback(async () => {
    const s = await getDBStats();
    setStats(s);
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    refreshStats();
    devlog.info('nav', 'Opened /dev page');
  }, [refreshStats]);

  return (
    <div className="dev-page">
      {/* Header */}
      <header className="dev-header">
        <h1 className="dev-title">Developer Tools</h1>
        <div className="dev-actions">
          <Link to="/log" className="btn btn-ghost">
            View Logs
          </Link>
          <Button variant="ghost" onClick={refreshStats}>
            Refresh
          </Button>
        </div>
      </header>

      {/* Warning Banner */}
      <div className="dev-warning">
        [DEV] This page is for development/debugging only. Not visible in production.
      </div>

      {/* Tabs */}
      <nav className="dev-tabs">
        {(['overview', 'import', 'data', 'sync', 'subscription', 'accounts', 'transactions', 'categories'] as TabId[]).map(
          (tab) => (
            <button
              key={tab}
              className={`dev-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          )
        )}
      </nav>

      {/* Tab Content */}
      <div className="dev-content">
        {activeTab === 'overview' && (
          <OverviewTab stats={stats} onOpenSubscription={() => setActiveTab('subscription')} />
        )}
        {activeTab === 'import' && <ImportTab onImportComplete={refreshStats} />}
        {activeTab === 'data' && <DataTab onDataChange={refreshStats} />}
        {activeTab === 'sync' && <SyncTab />}
        {activeTab === 'subscription' && <SubscriptionTab />}
        {activeTab === 'accounts' && <AccountsTab key={refreshKey} />}
        {activeTab === 'transactions' && <TransactionsTab key={refreshKey} />}
        {activeTab === 'categories' && <CategoriesTab key={refreshKey} />}
      </div>
    </div>
  );
}

// ============================================================
// Overview Tab
// ============================================================

// Relative age for glance rows and webhook events ("3h ago")
function relativeAge(value: string | null | undefined): string {
  if (!value) return '-';
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return '-';
  const mins = Math.max(0, Math.round((Date.now() - time) / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function isWithinDays(value: string | null | undefined, days: number): boolean {
  if (!value) return false;
  const time = new Date(value).getTime();
  return Number.isFinite(time) && Date.now() - time < days * 86400000;
}

interface GlanceData {
  stats: SubscriptionStats | null;
  latestWebhook: WebhookEvent | null;
  apiOk: boolean | null;
  apiRevision: string | null;
  pendingSync: number;
}

function OverviewTab({
  stats,
  onOpenSubscription,
}: {
  stats: { accounts: number; transactions: number; categories: number; budgets: number } | null;
  onOpenSubscription: () => void;
}) {
  const logStats = devlog.getStats();

  // Business at a glance - read-only pulls so the owner sees subscriptions,
  // newest Polar activity, API health, and stuck local sync WITHOUT
  // tab-hopping. Failures degrade gracefully (each section independent).
  const [glance, setGlance] = useState<GlanceData | null>(null);
  const glanceApiUrl = localStorage.getItem('ninjacash_api_url') || 'https://api.ninjacash.app';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result: GlanceData = {
        stats: null,
        latestWebhook: null,
        apiOk: null,
        apiRevision: null,
        pendingSync: 0,
      };

      // Local pending-sync records (stuck sync surfaces here first)
      try {
        const [txns, accts, holdings] = await Promise.all([
          db.transactions.filter((t) => t.syncStatus === 'pending').count(),
          db.accounts.filter((a) => a.syncStatus === 'pending').count(),
          db.holdingsAdjustments.filter((h) => h.syncStatus === 'pending').count(),
        ]);
        result.pendingSync = txns + accts + holdings;
      } catch { /* table missing during schema upgrade - skip */ }

      // Public API health (no auth needed)
      try {
        const healthRes = await fetch(`${glanceApiUrl}/health`);
        result.apiOk = healthRes.ok;
        if (healthRes.ok) {
          const health = await healthRes.json().catch(() => ({}));
          result.apiRevision = typeof health.apiRevision === 'string' ? health.apiRevision : null;
        }
      } catch {
        result.apiOk = false;
      }

      // Owner-only subscription stats + newest Polar event
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (token) {
          const headers = { Authorization: `Bearer ${token}` };
          const [statsRes, webhookRes] = await Promise.all([
            fetch(`${glanceApiUrl}/api/dev/subscription-stats`, { headers }),
            fetch(`${glanceApiUrl}/api/dev/webhooks?limit=1`, { headers }),
          ]);
          if (statsRes.ok) result.stats = await statsRes.json();
          if (webhookRes.ok) {
            const webhookData = await webhookRes.json();
            result.latestWebhook = webhookData.webhooks?.[0] || null;
          }
        }
      } catch (err) {
        console.warn('[DEV] Glance subscription fetch failed:', err);
      }

      if (!cancelled) setGlance(result);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newSubscriptionSignal =
    glance?.latestWebhook && isWithinDays(glance.latestWebhook.receivedAt, 7);

  return (
    <div className="tab-content">
      {/* Business at a Glance - subscriptions/API/sync without tab-hopping */}
      <Card>
        <CardHeader
          title="Business at a Glance"
          subtitle={`${glanceApiUrl.includes('localhost') ? 'LOCAL' : 'PRODUCTION'} · ${glanceApiUrl}`}
        />
        {!glance ? (
          <p>Loading...</p>
        ) : (
          <>
            {glance.stats && (
              <div className="stats-grid">
                <div className="stat-box stat-info">
                  <div className="stat-value">{glance.stats.totalPaid}</div>
                  <div className="stat-label">Total Paid</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{glance.stats.monthlyActiveCount}</div>
                  <div className="stat-label">Monthly</div>
                </div>
                <div className="stat-box">
                  <div className="stat-value">{glance.stats.lifetimeCount}</div>
                  <div className="stat-label">Lifetime ({glance.stats.lifetimeRemaining} left)</div>
                </div>
                <div className="stat-box stat-warn">
                  <div className="stat-value">{glance.stats.countsByStatus.trialing}</div>
                  <div className="stat-label">Trialing</div>
                </div>
              </div>
            )}

            {glance.stats && (
              <div className="glance-pills">
                <span className="glance-pill">Free: {glance.stats.countsByStatus.free}</span>
                <span className="glance-pill">Cancelled: {glance.stats.countsByStatus.cancelled}</span>
                <span className={`glance-pill ${glance.stats.countsByStatus.past_due > 0 ? 'bad' : ''}`}>
                  Past due: {glance.stats.countsByStatus.past_due}
                </span>
              </div>
            )}

            {/* Newest Polar activity - impossible to miss a fresh subscription */}
            <button
              type="button"
              className={`glance-event ${newSubscriptionSignal ? 'recent' : ''}`}
              onClick={onOpenSubscription}
              title="Open Subscription tab"
            >
              {newSubscriptionSignal && <span className="glance-new">NEW</span>}
              {glance.latestWebhook ? (
                <>
                  Latest Polar activity: <strong>{glance.latestWebhook.eventType}</strong>
                  {' '}· {relativeAge(glance.latestWebhook.receivedAt)}
                </>
              ) : (
                'No Polar webhook events yet'
              )}
              <span className="glance-event-arrow">›</span>
            </button>

            <div className="glance-row">
              <span className={`glance-dot ${glance.apiOk === null ? '' : glance.apiOk ? 'ok' : 'bad'}`} />
              <span>
                API {glance.apiOk === null ? 'unknown' : glance.apiOk ? 'healthy' : 'DOWN'}
                {glance.apiRevision ? ` · ${glance.apiRevision}` : ''}
              </span>
              <span className="glance-sep">|</span>
              <span className={glance.pendingSync > 0 ? 'glance-sync-warn' : 'glance-sync-ok'}>
                {glance.pendingSync > 0
                  ? `${glance.pendingSync} records pending sync`
                  : 'All local records synced'}
              </span>
            </div>
          </>
        )}
      </Card>

      <Card>
        <CardHeader title="Database Statistics" />
        {stats ? (
          <div className="stats-grid">
            <div className="stat-box">
              <div className="stat-value">{stats.accounts}</div>
              <div className="stat-label">Accounts</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">{stats.transactions}</div>
              <div className="stat-label">Transactions</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">{stats.categories}</div>
              <div className="stat-label">Categories</div>
            </div>
            <div className="stat-box">
              <div className="stat-value">{stats.budgets}</div>
              <div className="stat-label">Budgets</div>
            </div>
          </div>
        ) : (
          <p>Loading...</p>
        )}
      </Card>

      <Card>
        <CardHeader title="Log Statistics" />
        <div className="stats-grid">
          <div className="stat-box">
            <div className="stat-value">{logStats.total}</div>
            <div className="stat-label">Total Logs</div>
          </div>
          <div className="stat-box stat-error">
            <div className="stat-value">{logStats.byLevel.error}</div>
            <div className="stat-label">Errors</div>
          </div>
          <div className="stat-box stat-warn">
            <div className="stat-value">{logStats.byLevel.warn}</div>
            <div className="stat-label">Warnings</div>
          </div>
          <div className="stat-box stat-info">
            <div className="stat-value">{logStats.byLevel.info}</div>
            <div className="stat-label">Info</div>
          </div>
        </div>
        <div className="card-footer">
          <Link to="/log" className="btn btn-secondary btn-sm">
            Open Log Viewer
          </Link>
        </div>
      </Card>

      <Card>
        <CardHeader title="Quick Actions" />
        <div className="quick-actions">
          <Link to="/settings" className="btn btn-secondary">
            Settings
          </Link>
          <Link to="/accounts" className="btn btn-secondary">
            Accounts
          </Link>
          <Link to="/transactions" className="btn btn-secondary">
            Transactions
          </Link>
          <Link to="/xp1" className="btn btn-secondary">
            Landing Pages
          </Link>
          <Link to="/metrics_dev" className="btn btn-secondary">
            Dev Metrics
          </Link>
          <Link to="/crypto" className="btn btn-secondary">
            Crypto
          </Link>
          <Button
            variant="danger"
            onClick={() => {
              Sentry.captureMessage('Sentry test from DevPage button');
              throw new Error('Sentry test error from DevPage!');
            }}
          >
            Test Sentry
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              try {
                const token = (await supabase.auth.getSession()).data.session?.access_token;
                const apiUrl = localStorage.getItem('ninjacash_api_url') || 'https://api.ninjacash.app';
                const res = await fetch(`${apiUrl}/api/dev/test-sentry`, {
                  method: 'POST',
                  headers: { Authorization: `Bearer ${token}` },
                });
                const data = await res.json();
                alert(`API Sentry test: ${res.status} - ${JSON.stringify(data)}`);
              } catch (err) {
                alert(`API Sentry test triggered error (check Sentry): ${err}`);
              }
            }}
          >
            Test API Sentry
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// Accounts Tab
// ============================================================

function AccountsTab() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    db.accounts.toArray().then((data) => {
      setAccounts(data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="loading">Loading accounts...</div>;

  return (
    <div className="tab-content">
      <Card>
        <CardHeader title="All Accounts" subtitle={`${accounts.length} accounts in database`} />
        {accounts.length === 0 ? (
          <p className="empty-text">No accounts found.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Balance</th>
                <th>Currency</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((acc) => (
                <tr key={acc.id}>
                  <td>
                    <strong>{acc.name}</strong>
                    {acc.fullName && <span className="subtext"> ({acc.fullName})</span>}
                  </td>
                  <td>{acc.type}</td>
                  <td className={acc.balance >= 0 ? 'positive' : 'negative'}>
                    {acc.balance.toLocaleString('en-US', {
                      style: 'currency',
                      currency: acc.currency || 'USD',
                    })}
                  </td>
                  <td>{acc.currency}</td>
                  <td>{acc.isActive ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// Transactions Tab
// ============================================================

function TransactionsTab() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);

  useEffect(() => {
    db.transactions
      .orderBy('date')
      .reverse()
      .limit(limit)
      .toArray()
      .then((data) => {
        setTransactions(data);
        setLoading(false);
      });
  }, [limit]);

  if (loading) return <div className="loading">Loading transactions...</div>;

  return (
    <div className="tab-content">
      <Card>
        <CardHeader
          title="Recent Transactions"
          subtitle={`Showing ${transactions.length} of latest transactions`}
        />
        <div className="limit-selector">
          Show:
          {[50, 100, 500, 1000].map((n) => (
            <button
              key={n}
              className={`limit-btn ${limit === n ? 'active' : ''}`}
              onClick={() => setLimit(n)}
            >
              {n}
            </button>
          ))}
        </div>
        {transactions.length === 0 ? (
          <p className="empty-text">No transactions found.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Description</th>
                <th>Currency</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id}>
                  <td>{new Date(tx.date).toLocaleDateString()}</td>
                  <td>
                    <span className={`type-badge type-${tx.type}`}>{tx.type}</span>
                  </td>
                  <td className={tx.type === 'income' ? 'positive' : 'negative'}>
                    {tx.type === 'income' ? '+' : '-'}
                    {tx.amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td>{tx.description || '(no description)'}</td>
                  <td>{tx.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// Sync Tab
// ============================================================

function SyncTab() {
  const [apiUrl, setApiUrlState] = useState(getApiUrl());
  const [connected, setConnected] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [serverStatus, setServerStatus] = useState<{
    lastSyncAt: string | null;
    counts?: { accounts: number; categories: number; transactions: number };
  } | null>(null);

  const lastLocalSync = getLastSyncAt();

  // Check connection on mount
  useEffect(() => {
    handleCheckConnection();
  }, []);

  const handleCheckConnection = async () => {
    setChecking(true);
    setSyncResult(null);
    try {
      const isConnected = await checkConnection();
      setConnected(isConnected);
      if (isConnected) {
        const status = await getSyncStatus();
        setServerStatus(status);
      }
    } catch {
      setConnected(false);
    } finally {
      setChecking(false);
    }
  };

  const handleApiUrlChange = (url: string) => {
    setApiUrlState(url);
    setApiUrl(url);
    setConnected(null);
    setServerStatus(null);
  };

  const handlePush = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await pushAll();
      if (result.success) {
        setSyncResult('[OK] Push complete - all data sent to server');
        handleCheckConnection(); // Refresh status
      } else {
        setSyncResult(`[ERROR] Push failed: ${result.error}`);
      }
    } catch (err) {
      setSyncResult(`[ERROR] ${err instanceof Error ? err.message : 'Push failed'}`);
    } finally {
      setSyncing(false);
    }
  };

  const handlePull = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await pull();
      if (result.success) {
        const counts = result.counts || { accounts: 0, categories: 0, transactions: 0 };
        setSyncResult(
          `[OK] Pull complete - received ${counts.accounts} accounts, ${counts.categories} categories, ${counts.transactions} transactions`
        );
        handleCheckConnection();
      } else {
        setSyncResult(`[ERROR] Pull failed: ${result.error}`);
      }
    } catch (err) {
      setSyncResult(`[ERROR] ${err instanceof Error ? err.message : 'Pull failed'}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleFullSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await fullSync();
      if (result.success) {
        setSyncResult('[OK] Full sync complete - push then pull finished');
        handleCheckConnection();
      } else {
        setSyncResult(`[ERROR] Full sync failed: ${result.error}`);
      }
    } catch (err) {
      setSyncResult(`[ERROR] ${err instanceof Error ? err.message : 'Sync failed'}`);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="tab-content">
      {/* API Configuration */}
      <Card>
        <CardHeader
          title="Sync Server"
          subtitle="Connect to backend for phone <-> PC sync"
        />
        <div className="sync-config">
          <label className="sync-label">API URL:</label>
          <input
            type="text"
            value={apiUrl}
            onChange={(e) => handleApiUrlChange(e.target.value)}
            className="sync-input"
            placeholder="http://localhost:3001"
          />
          <Button
            variant="secondary"
            onClick={handleCheckConnection}
            disabled={checking}
          >
            {checking ? 'Checking...' : 'Test Connection'}
          </Button>
        </div>

        {/* Connection Status */}
        <div className={`sync-status ${connected === true ? 'connected' : connected === false ? 'disconnected' : ''}`}>
          {connected === null && 'Not checked'}
          {connected === true && '[OK] Connected to server'}
          {connected === false && '[FAILED] Cannot reach server - is run_api.bat running?'}
        </div>

        {/* Server Stats */}
        {serverStatus && connected && (
          <div className="sync-server-stats">
            <div className="stat-row">
              <span>Server last sync:</span>
              <span>{serverStatus.lastSyncAt ? new Date(serverStatus.lastSyncAt).toLocaleString() : 'Never'}</span>
            </div>
            {serverStatus.counts && (
              <>
                <div className="stat-row">
                  <span>Server accounts:</span>
                  <span>{serverStatus.counts.accounts}</span>
                </div>
                <div className="stat-row">
                  <span>Server categories:</span>
                  <span>{serverStatus.counts.categories}</span>
                </div>
                <div className="stat-row">
                  <span>Server transactions:</span>
                  <span>{serverStatus.counts.transactions}</span>
                </div>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Sync Actions */}
      <Card>
        <CardHeader
          title="Sync Actions"
          subtitle={`Last local sync: ${lastLocalSync ? lastLocalSync.toLocaleString() : 'Never'}`}
        />
        <div className="sync-actions">
          <Button
            variant="primary"
            onClick={handleFullSync}
            disabled={syncing || !connected}
          >
            {syncing ? 'Syncing...' : 'Full Sync (Push + Pull)'}
          </Button>
          <Button
            variant="secondary"
            onClick={handlePush}
            disabled={syncing || !connected}
          >
            Push Only
          </Button>
          <Button
            variant="secondary"
            onClick={handlePull}
            disabled={syncing || !connected}
          >
            Pull Only
          </Button>
        </div>

        {syncResult && (
          <p className={`sync-result ${syncResult.includes('[OK]') ? 'success' : 'error'}`}>
            {syncResult}
          </p>
        )}
      </Card>

      {/* Troubleshooting */}
      <Card>
        <CardHeader
          title="Troubleshooting"
          subtitle="Fix sync issues when data isn't populating"
        />
        <div className="sync-actions">
          <Button
            variant="secondary"
            onClick={async () => {
              setSyncing(true);
              setSyncResult(null);
              resetSyncState();
              setSyncResult('[OK] Sync state reset - lastSyncAt cleared');
              setSyncing(false);
            }}
            disabled={syncing}
          >
            Reset Sync State
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              setSyncing(true);
              setSyncResult(null);
              try {
                const result = await forceFreshPull();
                if (result.success) {
                  const counts = result.counts || { accounts: 0, categories: 0, transactions: 0 };
                  setSyncResult(
                    `[OK] Fresh pull complete - received ${counts.accounts} accounts, ${counts.categories} categories, ${counts.transactions} transactions`
                  );
                  handleCheckConnection();
                } else {
                  setSyncResult(`[ERROR] Fresh pull failed: ${result.error}`);
                }
              } catch (err) {
                setSyncResult(`[ERROR] ${err instanceof Error ? err.message : 'Fresh pull failed'}`);
              } finally {
                setSyncing(false);
              }
            }}
            disabled={syncing || !connected}
          >
            Force Fresh Pull (Get All Data)
          </Button>
        </div>
        <p className="sync-note" style={{ marginTop: 'var(--sp-3)' }}>
          Use "Reset Sync State" + "Force Fresh Pull" if your device shows 0 accounts after syncing.
          This clears the local timestamp and fetches ALL data from server.
        </p>
      </Card>

      {/* Auto-Sync Settings (owner only) */}
      <SyncSettingsPanel />

      {/* Help */}
      <Card>
        <CardHeader title="How to Sync" />
        <div className="sync-help">
          <ol>
            <li><strong>Start API server:</strong> Run <code>run_api.bat</code> on your PC</li>
            <li><strong>Test connection:</strong> Click "Test Connection" above</li>
            <li><strong>Push data:</strong> Send this device's data to server</li>
            <li><strong>Pull data:</strong> Get data from server to this device</li>
            <li><strong>Full sync:</strong> Push first, then pull (recommended)</li>
          </ol>
          <p className="sync-note">
            For phone: Use ngrok URL (e.g., https://xxx.ngrok-free.app) as API URL
          </p>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// Subscription Tab (Polar Payment Debugging)
// ============================================================

interface SubscriptionData {
  userId: string;
  email: string;
  prisma: {
    status: string;
    polarCustomerId?: string;
    polarSubscriptionId?: string;
    polarOrderId?: string;
    trialEndDate?: string;
    currentPeriodEnd?: string;
    pastDueSince?: string;
    createdAt?: string;
    updatedAt?: string;
  } | null;
  supabase: {
    subscriptionStatus: string;
    polarCustomerId?: string;
    trialEndDate?: string;
    updatedAt?: string;
  };
  isConsistent: boolean;
}

interface WebhookEvent {
  id: string;
  eventType: string;
  receivedAt: string;
}

interface LifetimeStats {
  count: number;
  cap: number;
  remaining: number | string;
  isCapReached: boolean;
  isCapEnabled: boolean;
  message: string;
}

interface SubscriptionStats {
  countsByStatus: {
    free: number;
    active: number;
    lifetime: number;
    trialing: number;
    cancelled: number;
    past_due: number;
  };
  monthlyActiveCount: number;
  lifetimeCount: number;
  totalPaid: number;
  totalWithTrial: number;
  lifetimeCap: number;
  lifetimeRemaining: number | string;
  isLifetimeCapReached: boolean;
  asOf: string;
}

function SubscriptionTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [webhooks, setWebhooks] = useState<WebhookEvent[]>([]);
  const [lifetimeStats, setLifetimeStats] = useState<LifetimeStats | null>(null);
  const [subscriptionStats, setSubscriptionStats] = useState<SubscriptionStats | null>(null);
  const [toggling, setToggling] = useState(false);
  const [toggleResult, setToggleResult] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [consistencyResult, setConsistencyResult] = useState<{ isConsistent: boolean; issues: string[] } | null>(null);

  // Get API base URL
  const getDevApiUrl = () => {
    const apiUrl = localStorage.getItem('ninjacash_api_url') || 'https://api.ninjacash.app';
    return apiUrl;
  };

  // Get auth token from existing supabase client
  const getAuthToken = async () => {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('[DEV] Failed to get session:', error);
      return undefined;
    }
    return data.session?.access_token;
  };

  // Load subscription data
  const loadSubscription = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const apiUrl = getDevApiUrl();
      
      // Fetch subscription status
      const subRes = await fetch(`${apiUrl}/api/dev/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!subRes.ok) {
        const errData = await subRes.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${subRes.status}`);
      }
      
      const subData = await subRes.json();
      setSubscription(subData);

      // Fetch webhook events
      const webhookRes = await fetch(`${apiUrl}/api/dev/webhooks?limit=10`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (webhookRes.ok) {
        const webhookData = await webhookRes.json();
        setWebhooks(webhookData.webhooks || []);
      }

      // Fetch lifetime stats (legacy)
      const lifetimeRes = await fetch(`${apiUrl}/api/dev/lifetime-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (lifetimeRes.ok) {
        const lifetimeData = await lifetimeRes.json();
        setLifetimeStats(lifetimeData);
      }

      // Fetch full subscription stats (new - includes monthly)
      const statsRes = await fetch(`${apiUrl}/api/dev/subscription-stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setSubscriptionStats(statsData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  // Toggle subscription status
  const handleToggle = async (status: string) => {
    setToggling(true);
    setToggleResult(null);
    try {
      const token = await getAuthToken();
      const apiUrl = getDevApiUrl();
      
      const res = await fetch(`${apiUrl}/api/dev/subscription/toggle`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      const data = await res.json();
      
      if (!res.ok) {
        setToggleResult(`[ERROR] ${data.error || 'Toggle failed'}`);
      } else {
        setToggleResult(`[OK] ${data.message}`);
        // Reload data
        await loadSubscription();
      }
    } catch (err) {
      setToggleResult(`[ERROR] ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setToggling(false);
    }
  };

  // Check consistency
  const handleConsistencyCheck = async () => {
    setSyncing(true);
    try {
      const token = await getAuthToken();
      const apiUrl = getDevApiUrl();
      
      const res = await fetch(`${apiUrl}/api/dev/subscription/consistency`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      setConsistencyResult(data);
    } catch (err) {
      setConsistencyResult({ isConsistent: false, issues: [String(err)] });
    } finally {
      setSyncing(false);
    }
  };

  // Sync Prisma -> Supabase
  const handleSync = async () => {
    setSyncing(true);
    setToggleResult(null);
    try {
      const token = await getAuthToken();
      const apiUrl = getDevApiUrl();
      
      const res = await fetch(`${apiUrl}/api/dev/subscription/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      
      if (!res.ok) {
        setToggleResult(`[ERROR] ${data.error || 'Sync failed'}`);
      } else {
        setToggleResult(`[OK] ${data.message}`);
        await loadSubscription();
      }
    } catch (err) {
      setToggleResult(`[ERROR] ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSyncing(false);
    }
  };

  // Load on mount
  useEffect(() => {
    loadSubscription();
  }, []);

  // Format date helper
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
      case 'lifetime':
        return 'var(--color-success)';
      case 'trialing':
        return 'var(--color-warning)';
      case 'past_due':
        return 'var(--color-error)';
      case 'cancelled':
        return 'var(--color-text-secondary)';
      default:
        return 'var(--color-text-tertiary)';
    }
  };

  if (loading) return <div className="loading">Loading subscription data...</div>;

  if (error) {
    return (
      <div className="tab-content">
        <Card>
          <CardHeader title="Subscription Dev Tools" subtitle="Debug Polar payment integration" />
          <div className="error-message">
            <p>[ERROR] {error}</p>
            <Button variant="secondary" onClick={loadSubscription}>Retry</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="tab-content">
      {/* Environment Indicator */}
      <div className="dev-env-banner">
        <span className="env-label">Environment:</span>
        <span className="env-value">{getDevApiUrl().includes('localhost') ? 'LOCAL' : 'PRODUCTION'}</span>
        <span className="env-api">{getDevApiUrl()}</span>
      </div>

      {/* Subscription Stats - Full breakdown of all subscription types */}
      {subscriptionStats && (
        <Card>
          <CardHeader 
            title="Subscription Stats" 
            subtitle={`Total paid: ${subscriptionStats.totalPaid} users`}
          />
          <div className="subscription-stats-display">
            {/* Main stats row */}
            <div className="subscription-stats-grid">
              <div className="subscription-stat-box monthly">
                <div className="subscription-stat-value">{subscriptionStats.monthlyActiveCount}</div>
                <div className="subscription-stat-label">Monthly</div>
              </div>
              <div className={`subscription-stat-box lifetime ${subscriptionStats.isLifetimeCapReached ? 'cap-reached' : ''}`}>
                <div className="subscription-stat-value">{subscriptionStats.lifetimeCount}</div>
                <div className="subscription-stat-label">Lifetime</div>
              </div>
              <div className="subscription-stat-box trialing">
                <div className="subscription-stat-value">{subscriptionStats.countsByStatus.trialing}</div>
                <div className="subscription-stat-label">Trialing</div>
              </div>
              <div className="subscription-stat-box total">
                <div className="subscription-stat-value">{subscriptionStats.totalPaid}</div>
                <div className="subscription-stat-label">Total Paid</div>
              </div>
            </div>

            {/* Lifetime cap progress */}
            {subscriptionStats.lifetimeCap > 0 && (
              <div className="lifetime-cap-section">
                <div className="lifetime-cap-header">
                  <span>Lifetime Cap</span>
                  <span>{subscriptionStats.lifetimeCount} / {subscriptionStats.lifetimeCap}</span>
                </div>
                <div className="lifetime-progress-bar">
                  <div 
                    className={`lifetime-progress-fill ${subscriptionStats.isLifetimeCapReached ? 'full' : ''}`}
                    style={{ width: `${Math.min(100, (subscriptionStats.lifetimeCount / subscriptionStats.lifetimeCap) * 100)}%` }}
                  />
                </div>
                <div className="lifetime-remaining">
                  {subscriptionStats.isLifetimeCapReached 
                    ? 'Cap reached - no more lifetime slots available'
                    : `${subscriptionStats.lifetimeRemaining} slots remaining`}
                </div>
              </div>
            )}

            {/* Secondary stats */}
            <div className="subscription-stats-secondary">
              <div className="stat-item">
                <span className="stat-name">Free users:</span>
                <span className="stat-count">{subscriptionStats.countsByStatus.free}</span>
              </div>
              <div className="stat-item">
                <span className="stat-name">Cancelled:</span>
                <span className="stat-count">{subscriptionStats.countsByStatus.cancelled}</span>
              </div>
              <div className="stat-item">
                <span className="stat-name">Past due:</span>
                <span className="stat-count warning">{subscriptionStats.countsByStatus.past_due}</span>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Legacy Lifetime Stats fallback (if new endpoint fails) */}
      {!subscriptionStats && lifetimeStats && (
        <Card>
          <CardHeader 
            title="Lifetime Subscriptions" 
            subtitle={lifetimeStats.isCapEnabled ? `Cap: ${lifetimeStats.cap} users` : 'Cap disabled'}
          />
          <div className="lifetime-stats-display">
            <div className={`lifetime-stat-box ${lifetimeStats.isCapReached ? 'cap-reached' : ''}`}>
              <div className="lifetime-stat-value">{lifetimeStats.count}</div>
              <div className="lifetime-stat-label">Lifetime Users</div>
            </div>
            {lifetimeStats.isCapEnabled && (
              <div className={`lifetime-stat-box ${lifetimeStats.isCapReached ? 'cap-reached' : 'remaining'}`}>
                <div className="lifetime-stat-value">
                  {typeof lifetimeStats.remaining === 'number' ? lifetimeStats.remaining : lifetimeStats.remaining}
                </div>
                <div className="lifetime-stat-label">Remaining Slots</div>
              </div>
            )}
            <div className="lifetime-progress">
              {lifetimeStats.isCapEnabled && (
                <div className="lifetime-progress-bar">
                  <div 
                    className={`lifetime-progress-fill ${lifetimeStats.isCapReached ? 'full' : ''}`}
                    style={{ width: `${Math.min(100, (lifetimeStats.count / lifetimeStats.cap) * 100)}%` }}
                  />
                </div>
              )}
              <div className={`lifetime-message ${lifetimeStats.isCapReached ? 'warning' : ''}`}>
                {lifetimeStats.message}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Subscription Status */}
      <Card>
        <CardHeader title="Subscription Status" subtitle="Current state from Prisma DB and Supabase" />
        
        {subscription && (
          <div className="subscription-grid">
            {/* User Info */}
            <div className="sub-section">
              <h4>User</h4>
              <div className="sub-row">
                <span className="sub-label">Email:</span>
                <span className="sub-value">{subscription.email}</span>
              </div>
              <div className="sub-row">
                <span className="sub-label">User ID:</span>
                <span className="sub-value sub-id">{subscription.userId}</span>
              </div>
            </div>

            {/* Prisma State */}
            <div className="sub-section">
              <h4>Prisma DB</h4>
              {subscription.prisma ? (
                <>
                  <div className="sub-row">
                    <span className="sub-label">Status:</span>
                    <span className="sub-status" style={{ color: getStatusColor(subscription.prisma.status) }}>
                      {subscription.prisma.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="sub-row">
                    <span className="sub-label">Polar Customer:</span>
                    <span className="sub-value sub-id">{subscription.prisma.polarCustomerId || '-'}</span>
                  </div>
                  <div className="sub-row">
                    <span className="sub-label">Polar Sub ID:</span>
                    <span className="sub-value sub-id">{subscription.prisma.polarSubscriptionId || '-'}</span>
                  </div>
                  <div className="sub-row">
                    <span className="sub-label">Period End:</span>
                    <span className="sub-value">{formatDate(subscription.prisma.currentPeriodEnd)}</span>
                  </div>
                  <div className="sub-row">
                    <span className="sub-label">Updated:</span>
                    <span className="sub-value">{formatDate(subscription.prisma.updatedAt)}</span>
                  </div>
                </>
              ) : (
                <p className="sub-empty">No subscription record</p>
              )}
            </div>

            {/* Supabase State */}
            <div className="sub-section">
              <h4>Supabase Metadata</h4>
              <div className="sub-row">
                <span className="sub-label">Status:</span>
                <span className="sub-status" style={{ color: getStatusColor(subscription.supabase.subscriptionStatus) }}>
                  {subscription.supabase.subscriptionStatus.toUpperCase()}
                </span>
              </div>
              <div className="sub-row">
                <span className="sub-label">Polar Customer:</span>
                <span className="sub-value sub-id">{subscription.supabase.polarCustomerId || '-'}</span>
              </div>
              <div className="sub-row">
                <span className="sub-label">Updated:</span>
                <span className="sub-value">{formatDate(subscription.supabase.updatedAt)}</span>
              </div>
            </div>

            {/* Consistency */}
            <div className="sub-section">
              <h4>Consistency</h4>
              <div className={`consistency-badge ${subscription.isConsistent ? 'consistent' : 'inconsistent'}`}>
                {subscription.isConsistent ? '[OK] In Sync' : '[MISMATCH] Out of Sync'}
              </div>
              {consistencyResult && !consistencyResult.isConsistent && (
                <ul className="consistency-issues">
                  {consistencyResult.issues.map((issue, i) => (
                    <li key={i}>{issue}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        <div className="sub-actions">
          <Button variant="secondary" onClick={loadSubscription}>Refresh</Button>
          <Button variant="secondary" onClick={handleConsistencyCheck} disabled={syncing}>
            {syncing ? 'Checking...' : 'Check Consistency'}
          </Button>
          <Button variant="secondary" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : 'Sync Prisma -> Supabase'}
          </Button>
        </div>
      </Card>

      {/* Manual Toggle */}
      <Card>
        <CardHeader 
          title="Manual Pro Toggle" 
          subtitle="Grant or revoke Pro status for testing (updates both Prisma + Supabase)"
        />
        <div className="toggle-buttons">
          <Button 
            variant="primary" 
            onClick={() => handleToggle('active')} 
            disabled={toggling}
          >
            Grant Pro (Active)
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => handleToggle('lifetime')} 
            disabled={toggling}
          >
            Grant Lifetime
          </Button>
          <Button 
            variant="secondary" 
            onClick={() => handleToggle('trialing')} 
            disabled={toggling}
          >
            Set Trialing
          </Button>
          <Button 
            variant="danger" 
            onClick={() => handleToggle('free')} 
            disabled={toggling}
          >
            Revoke (Free)
          </Button>
        </div>
        {toggleResult && (
          <p className={`toggle-result ${toggleResult.includes('[OK]') ? 'success' : 'error'}`}>
            {toggleResult}
          </p>
        )}
        <p className="toggle-note">
          After toggling, refresh your browser session (or sign out/in) to see UI changes.
        </p>
      </Card>

      {/* Webhook Events */}
      <Card>
        <CardHeader 
          title="Recent Webhook Events" 
          subtitle={`Last ${webhooks.length} processed Polar webhooks`}
        />
        {webhooks.length === 0 ? (
          <p className="empty-text">No webhook events recorded yet.</p>
        ) : (
          <table className="data-table webhook-table">
            <thead>
              <tr>
                <th>Event ID</th>
                <th>Type</th>
                <th>Received</th>
              </tr>
            </thead>
            <tbody>
              {webhooks.map((wh) => (
                <tr key={wh.id}>
                  <td className="sub-id">{wh.id.substring(0, 20)}...</td>
                  <td>
                    <span className={`event-type ${wh.eventType.replace('.', '-')}`}>
                      {wh.eventType}
                    </span>
                  </td>
                  <td>
                    {formatDate(wh.receivedAt)}
                    <span className="webhook-age">{relativeAge(wh.receivedAt)}</span>
                    {isWithinDays(wh.receivedAt, 7) && <span className="glance-new">NEW</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Quick Links */}
      <Card>
        <CardHeader title="Quick Links" subtitle="External dashboards for debugging" />
        <div className="quick-links">
          <a 
            href="https://polar.sh/dashboard" 
            target="_blank" 
            rel="noopener noreferrer"
            className="quick-link"
          >
            Polar Dashboard
          </a>
          <a 
            href="https://supabase.com/dashboard" 
            target="_blank" 
            rel="noopener noreferrer"
            className="quick-link"
          >
            Supabase Dashboard
          </a>
          <a 
            href="https://railway.app" 
            target="_blank" 
            rel="noopener noreferrer"
            className="quick-link"
          >
            Railway Logs
          </a>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// Categories Tab
// ============================================================

function CategoriesTab() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    db.categories.toArray().then((data) => {
      setCategories(data);
      setLoading(false);
    });
  }, []);

  if (loading) return <div className="loading">Loading categories...</div>;

  const incomeCategories = categories.filter((c) => c.type === 'income');
  const expenseCategories = categories.filter((c) => c.type === 'expense');

  return (
    <div className="tab-content">
      <Card>
        <CardHeader
          title="Income Categories"
          subtitle={`${incomeCategories.length} categories`}
        />
        {incomeCategories.length === 0 ? (
          <p className="empty-text">No income categories.</p>
        ) : (
          <ul className="category-list">
            {incomeCategories.map((cat) => (
              <li key={cat.id} className="category-item income">
                {cat.name}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader
          title="Expense Categories"
          subtitle={`${expenseCategories.length} categories`}
        />
        {expenseCategories.length === 0 ? (
          <p className="empty-text">No expense categories.</p>
        ) : (
          <ul className="category-list">
            {expenseCategories.map((cat) => (
              <li key={cat.id} className="category-item expense">
                {cat.name}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
